
import { PermissionsState } from '@/types/permissions';
import { CACHE_CONFIG, CacheEntry } from './cacheConfig';
import { getCacheKey } from './cacheKeys';
import { isCacheExpired, isCacheVersionValid } from './cacheValidation';
import { clearUserCache } from './cacheMaintenance';
import { permissionEventBus } from '../events/permissionEventBus';

/**
 * Enhanced cache entry with event metadata
 */
interface EnhancedCacheEntry extends CacheEntry {
  lastEventTimestamp?: number;
  invalidationReason?: string;
  source?: 'api' | 'cache' | 'fallback';
}

/**
 * Get cached permissions with enhanced validation
 */
export const getEnhancedCachedPermissions = (
  userId: string, 
  version: string = CACHE_CONFIG.VERSION
): PermissionsState | null => {
  if (!userId) {
    console.warn('getEnhancedCachedPermissions: No userId provided');
    return null;
  }

  try {
    const cacheKey = getCacheKey(userId, version);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      console.log(`📦 No cache found for user ${userId}`);
      return null;
    }
    
    const entry: EnhancedCacheEntry = JSON.parse(cached);
    
    // Validate cache version
    if (!isCacheVersionValid(entry, version)) {
      console.log(`🔄 Cache version mismatch for user ${userId}, clearing cache`);
      clearUserCache(userId);
      return null;
    }
    
    // Check expiration
    if (isCacheExpired(entry)) {
      console.log(`⏰ Cache expired for user ${userId}, clearing cache`);
      localStorage.removeItem(cacheKey);
      return null;
    }
    

    
    return entry.permissions;
  } catch (error) {
    console.error('❌ Error reading enhanced permissions cache:', error);
    clearUserCache(userId);
    return null;
  }
};

/**
 * Set enhanced cached permissions with event metadata
 */
export const setEnhancedCachedPermissions = (
  userId: string,
  permissions: PermissionsState,
  options: {
    customTTL?: number;
    version?: string;
    source?: 'api' | 'cache' | 'fallback';
    eventTimestamp?: number;
    invalidationReason?: string;
  } = {}
): void => {
  if (!userId) {
    console.warn('setEnhancedCachedPermissions: No userId provided');
    return;
  }

  try {
    const {
      customTTL,
      version = CACHE_CONFIG.VERSION,
      source = 'api',
      eventTimestamp,
      invalidationReason
    } = options;

    const ttl = customTTL || CACHE_CONFIG.DEFAULT_TTL;
    
    const entry: EnhancedCacheEntry = {
      permissions,
      expiresAt: Date.now() + ttl,
      version,
      createdAt: Date.now(),
      userId,
      lastEventTimestamp: eventTimestamp,
      invalidationReason,
      source
    };
    
    const cacheKey = getCacheKey(userId, version);
    localStorage.setItem(cacheKey, JSON.stringify(entry));
    

  } catch (error) {
    console.error('❌ Error setting enhanced permissions cache:', error);
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('📦 Storage quota exceeded, clearing old cache entries');
      import('./cacheMaintenance').then(({ clearExpiredCache }) => {
        clearExpiredCache();
      });
    }
  }
};

/**
 * Invalidate cache for specific user due to permission event
 */
export const invalidateUserPermissionsCache = (
  userId: string,
  reason: string,
  eventTimestamp?: number
): void => {

  
  try {
    clearUserCache(userId);
    
    // Optionally store a marker indicating why cache was invalidated
    const invalidationMarker = {
      userId,
      invalidatedAt: Date.now(),
      reason,
      eventTimestamp
    };
    
    localStorage.setItem(
      `${CACHE_CONFIG.PREFIX}_invalidation_${userId}`, 
      JSON.stringify(invalidationMarker)
    );
    

  } catch (error) {
    console.error('❌ Error invalidating user permissions cache:', error);
  }
};

/**
 * Check if cache was recently invalidated
 */
export const wasRecentlyInvalidated = (userId: string, withinMs: number = 60000): boolean => {
  try {
    const marker = localStorage.getItem(`${CACHE_CONFIG.PREFIX}_invalidation_${userId}`);
    if (!marker) return false;
    
    const { invalidatedAt } = JSON.parse(marker);
    return Date.now() - invalidatedAt < withinMs;
  } catch {
    return false;
  }
};

/**
 * Setup event bus listeners for cache invalidation
 */
export const setupCacheEventListeners = (): (() => void) => {
  
  const unsubscribe = permissionEventBus.onAny((event) => {
    console.log('🔔 Cache received permission event:', event);
    
    // Invalidate cache for the affected user
    invalidateUserPermissionsCache(
      event.userId,
      `${event.type} from ${event.source}`,
      event.timestamp
    );
  });
  
  return unsubscribe;
};
